import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ShieldCheck } from "lucide-react";
import { listAdmins, createAdmin, deleteAdmin, formatApiError } from "@/lib/cms";
import { useAuth } from "@/context/AuthContext";

export const AdminsManager = () => {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = () => listAdmins().then(setAdmins).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createAdmin(form);
      toast.success("Admin created");
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not create admin"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove admin ${a.email}?`)) return;
    try {
      await deleteAdmin(a.id);
      toast.success("Admin removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not remove admin"));
    }
  };

  const inp = "w-full rounded-lg border-2 border-input bg-background px-3 py-2 text-sm";

  return (
    <div data-testid="admins-manager">
      <h2 className="mb-6 font-heading text-2xl font-bold tracking-tight text-foreground">Admin Accounts</h2>
      <div className="mb-8 divide-y divide-border border border-border">
        {admins.map((a) => (
          <div key={a.id} className="flex items-center gap-4 bg-card p-4" data-testid={`admin-row-${a.id}`}>
            <ShieldCheck className="h-5 w-5 text-primary" />
            <div className="min-w-0 flex-1">
              <div className="truncate font-bold text-foreground">{a.name} {a.email === user?.email && <span className="text-xs text-primary">(you)</span>}</div>
              <div className="truncate text-xs text-muted-foreground">{a.email}</div>
            </div>
            {a.email !== user?.email && (
              <button onClick={() => remove(a)} className="rounded-lg border border-border p-2 text-destructive hover:border-destructive">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      <form onSubmit={add} className="max-w-lg space-y-4 border border-border bg-card p-6" data-testid="create-admin-form">
        <h3 className="font-heading text-lg font-bold tracking-tight">Create New Admin</h3>
        <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} data-testid="new-admin-name" />
        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} data-testid="new-admin-email" />
        <input required type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inp} data-testid="new-admin-password" />
        <button type="submit" disabled={saving} data-testid="create-admin-btn" className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-white hover:bg-primary/90 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> Create Admin</>}
        </button>
      </form>
    </div>
  );
};
